import { useMemo } from "react";

import { calculateScore } from "../utils/calculateScore";
import { Team } from "../../game/Types/TeamTypes";
import { Player } from "../../game/Types/GameTypes_dep";

export function useTeamResultAnalytics(teams: Team[], players: Player[]) {
  const numberOfParticipants = useMemo(() => {
    return players.length;
  }, [players]);

  const average = useMemo(() => {
    const total = players.reduce((acc, player) => {
      const team = teams.find((team) => team.id === player.groupId);
      if (!team) return acc;

      return acc + calculateScore(team.gameData.answerResults);
    }, 0);
    return total / players.length;
  }, [teams, players]);

  const highestScore = useMemo(() => {
    if (!teams) return 0;
    const highestScore = teams.reduce((acc, result) => {
      const score = calculateScore(result.gameData.answerResults);
      if (score > acc) {
        return score;
      } else {
        return acc;
      }
    }, 0);
    return highestScore;
  }, [teams]);

  const lowestScore = useMemo(() => {
    if (!teams) return 0;
    const lowestScore = teams.reduce((acc, result) => {
      const score = calculateScore(result.gameData.answerResults);
      if (score < acc) {
        return score;
      } else {
        return acc;
      }
    }, 100);
    return lowestScore;
  }, [teams]);

  const highScorers = useMemo(() => {
    // return an array of studentIds that have a score of 80 or higher
    if (!teams) return [];
    const highScoringStudents = teams.reduce((acc, result) => {
      const score = calculateScore(result.gameData.answerResults);
      if (score >= 80) {
        return [...acc, result.id];
      } else {
        return acc;
      }
    }, [] as string[]);
    return highScoringStudents;
  }, [teams]);

  const lowScorers = useMemo(() => {
    // return an array of studentIds that have a score of 50 or lower
    if (!teams) return [];
    const lowScoringStudents = teams.reduce((acc, result) => {
      const score = calculateScore(result.gameData.answerResults);
      if (score < 50) {
        return [...acc, result.id];
      } else {
        return acc;
      }
    }, [] as string[]);
    return lowScoringStudents;
  }, [teams]);

  const midScorers = useMemo(() => {
    // return an array of studentIds that have a score between 50 and 80
    if (!teams) return [];
    const midScoringStudents = teams.reduce((acc, result) => {
      const score = calculateScore(result.gameData.answerResults);
      if (score >= 50 && score < 80) {
        return [...acc, result.id];
      } else {
        return acc;
      }
    }, [] as string[]);
    return midScoringStudents;
  }, [teams]);

  return {
    average,
    numberOfParticipants,
    highestScore,
    lowestScore,
    highScorers,
    midScorers,
    lowScorers,
  };
}
